/**
 * The approach for the zoom effect:
 * - render a hidden clone (opacity 0) of the camera image so we know the initial render width and height of the camera image
 * - compute the maximum allowed scale value (having the clone camera image as reference) for the current viewport size
 *   so we avoid scaling the image more than the viewport size (as we cannot control the scaling limit)
 * - use that maximum scale value and update the width and height of the original camera image by multiplying the initial
 *   width and height with that maximum scale value in order to have the camera image rendered to its maximum scaling size
 *   (with this we avoid blurry images on Safari caused by images that are initially rendered small and then are scaled up)
 * - whenever the maximum scale value changes (e.g. viewport is changed when device orientation is changed or window is resized)
 *   compute the minimum scale value (1/maxScaleValue) so we basically render the camera image the same size it initially had
 *   (the difference is that initially the camera image was rendered by default, while now it is rendered by being scaled down from
 *   its max)
 * - we now bind the scaling to scrolling so we start from this minimum scale value up to 1 (where 1 represents
 *   the camera image having the maximum width and height)
 * - after the camera image (together with the video thumbnail) reaches the full scale, because we need to keep expanding the
 *   video thumbnail to fullscreen and because the video thumbnail may not have the same aspect ratio as the screen,
 *   we have a clone for the video thumbnail that we keep expanding up to fullscreen
 */

import HueRed from '@assets/hue_red.svg';
import HueYellow from '@assets/hue_yellow.svg';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { IVideo } from '@models/IVideo';
import GlowColourEnum from '@models/enums/GlowColourEnum';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface IVideoThumbnailPosition {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

interface Props {
  readonly title: string;
  readonly subTitle: string;
  readonly text: string;
  readonly desktopCameraImage: IImage;
  readonly mobileCameraImage: IImage;
  readonly desktopVideoThumbnail: IImage;
  readonly mobileVideoThumbnail: IImage;
  readonly desktopVideoThumbnailPosition: IVideoThumbnailPosition;
  readonly mobileVideoThumbnailPosition: IVideoThumbnailPosition;
  readonly desktopVideo: IVideo;
  readonly mobileVideo: IVideo;
  readonly glow?: GlowColourEnum;
}

const ImmersiveVideo = ({
  title,
  subTitle,
  text,
  desktopCameraImage,
  mobileCameraImage,
  desktopVideoThumbnail,
  mobileVideoThumbnail,
  desktopVideoThumbnailPosition,
  mobileVideoThumbnailPosition,
  desktopVideo,
  mobileVideo,
  glow = GlowColourEnum.None,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const isBelowDesktop = useMediaQuery(1024);
  const cameraImage = isBelowDesktop ? mobileCameraImage : desktopCameraImage;
  const videoThumbnail = isBelowDesktop
    ? mobileVideoThumbnail
    : desktopVideoThumbnail;
  const {
    top: videoThumbnailTopPercentage,
    bottom: videoThumbnailBottomPercentage,
    left: videoThumbnailLeftPercentage,
    right: videoThumbnailRightPercentage,
  } = isBelowDesktop
    ? mobileVideoThumbnailPosition
    : desktopVideoThumbnailPosition;
  const video = isBelowDesktop ? mobileVideo : desktopVideo;

  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);
  const [titleRef, { height: titleHeight }] = useResizeObserver();
  const [titleContainerHeight, setTitleContainerHeight] = useState(0);
  const [isComponentOnTop, setIsComponentOnTop] = useState<boolean>();
  const viewportContainerRef = useRef<HTMLDivElement | null>(null);
  const [
    viewportContainerRefCallback,
    { width: viewportWidth, height: viewportHeight },
  ] = useResizeObserver();
  const [
    initialCameraImageRef,
    { width: initialCameraImageWidth, height: initialCameraImageHeight },
  ] = useResizeObserver();

  const {
    siteNotificationsHeight,
    tabbedProductNavigationTopHeight,
    tabbedProductNavigationBottomHeight: viewportBottomOffset,
  } = useComponentsHeightsStore((state) => ({
    siteNotificationsHeight: state.siteNotificationsHeight,
    tabbedProductNavigationTopHeight: state.tabbedProductNavigationTopHeight,
    tabbedProductNavigationBottomHeight:
      state.tabbedProductNavigationBottomHeight,
  }));
  const viewportTopOffset =
    isBelowDesktop === false
      ? siteNotificationsHeight + tabbedProductNavigationTopHeight
      : 0;

  const [isCameraImageDisplayed, setIsCameraImageDisplayed] = useState(false);
  const [textDetailsRef, { height: textDetailsHeight }] = useResizeObserver();
  const [textRef, { height: textHeight }] = useResizeObserver();
  const textContainerRef = useRef<HTMLDivElement | null>(null);

  // The video thumbnail clone is basically the same image as the video thumbnail but we position it
  // relative to the viewport so we can expand it up to fullscreen, as by just scaling the original
  // video thumbnail we do not have control of the scaling so it scales up to fit the viewport exactly
  const [isVideoThumbnailCloneDisplayed, setIsVideoThumbnailCloneDisplayed] =
    useState(false);
  const [isVideoDisplayed, setIsVideoDisplayed] = useState(false);
  const [cameraImageWidth, setCameraImageWidth] = useState(0);
  const [cameraImageHeight, setCameraImageHeight] = useState(0);
  const [videoIsFocusedByTab, setVideoIsFocusedByTab] = useState(false);
  const isPreview = usePreviewMode();

  const { scrollYProgress } = useScroll({
    target: scrollableContainerRef,
    offset: ['start center', 'end end'],
  });
  const smoothScrollYProgress = useSpring(scrollYProgress, {
    stiffness: 500,
    damping: 50,
  });

  // Because the editor adds in the CMS the top, bottom, left and right position of the videoThumbnail within the camera image,
  // we also need to compute the actual width and height (first in percentage and then in actual px value) of the videoThumbnail
  const videoThumbnailWidthPercentage =
    100 - videoThumbnailLeftPercentage - videoThumbnailRightPercentage;
  const videoThumbnailWidth =
    (videoThumbnailWidthPercentage / 100) * cameraImageWidth;
  const initialVideoThumbnailWidth =
    (videoThumbnailWidthPercentage / 100) * initialCameraImageWidth;

  const videoThumbnailHeightPercentage =
    100 - videoThumbnailTopPercentage - videoThumbnailBottomPercentage;
  const videoThumbnailHeight =
    (videoThumbnailHeightPercentage / 100) * cameraImageHeight;
  const initialVideoThumbnailHeight =
    (videoThumbnailWidthPercentage / 100) * initialCameraImageHeight;

  // We want to make sure that by scaling the camera image (together with the videoThumbnail) we do not want to overflow the viewport,
  // so we just make it scale up to max 0.8 on desktop or 0.9 on mobile (random value chosen to make it look ok)
  // of either viewport width or height, whichever is smaller
  const maxScaleSizeThreshold = isMobile ? 0.9 : 0.8;
  const maxHeightScale =
    initialVideoThumbnailHeight > 0
      ? (maxScaleSizeThreshold * viewportHeight) / initialVideoThumbnailHeight
      : 0;
  const maxWidthScale =
    initialVideoThumbnailWidth > 0
      ? (maxScaleSizeThreshold * viewportWidth) / initialVideoThumbnailWidth
      : 0;
  const maxCameraScale = isPreview
    ? 1
    : Math.min(maxHeightScale, maxWidthScale);
  const minCameraScale = maxCameraScale > 0 ? 1 / maxCameraScale : 0;

  const cameraImageScale = useTransform(
    smoothScrollYProgress,
    [0.2, 0.5],
    [minCameraScale > 0 ? minCameraScale : 1, 1]
  );

  const cameraImageOpacity = useTransform(
    smoothScrollYProgress,
    [0.3, 0.5],
    [0, isPreview ? 0 : 1]
  );
  const videoThumbnailCloneBorderRadius = useTransform(
    smoothScrollYProgress,
    [0.5, 0.6],
    [5 * maxCameraScale, 0]
  );
  const videoGradientOpacity = useTransform(
    smoothScrollYProgress,
    [0.8, 0.9],
    [0, 1]
  );
  const textGradientOpacity = useTransform(
    smoothScrollYProgress,
    [0.8, 0.9],
    [0, 1]
  );
  const textDetailsBottomPosition = useTransform(
    smoothScrollYProgress,
    [0.85, 0.95],
    [-textDetailsHeight, 0]
  );

  const initialVideoThumbnailCloneXPadding =
    (viewportWidth - videoThumbnailWidth) / 2;
  const initialVideoThumbnailCloneYPadding =
    (viewportHeight - videoThumbnailHeight) / 2;
  const videoThumbnailCloneXPadding = useTransform(
    smoothScrollYProgress,
    [0.5, 0.7],
    [initialVideoThumbnailCloneXPadding, 0]
  );
  const videoThumbnailCloneYPadding = useTransform(
    smoothScrollYProgress,
    [0.5, 0.7],
    [initialVideoThumbnailCloneYPadding, 0]
  );

  const cameraImageTransformOriginLeft =
    videoThumbnailLeftPercentage + videoThumbnailWidthPercentage / 2;
  const cameraImageTransformOriginTop =
    videoThumbnailTopPercentage + videoThumbnailHeightPercentage / 2;

  const cameraImageLeftPosition =
    viewportWidth / 2 -
    (cameraImageTransformOriginLeft / 100) * cameraImageWidth;
  const cameraImageFinalBottomPosition =
    viewportHeight / 2 -
    ((100 - cameraImageTransformOriginTop) / 100) * cameraImageHeight;
  const cameraImageBottomPosition = useTransform(
    smoothScrollYProgress,
    [0, 0.2],
    [
      isPreview
        ? cameraImageFinalBottomPosition
        : cameraImageFinalBottomPosition -
          initialCameraImageHeight -
          (cameraImageTransformOriginTop / 100) * initialCameraImageHeight -
          (isBelowDesktop ? titleHeight / 2 : 0),
      cameraImageFinalBottomPosition,
    ]
  );

  const videoThumbnailTopValue =
    (videoThumbnailTopPercentage / 100) * cameraImageHeight;
  const videoThumbnailLeftValue =
    (videoThumbnailLeftPercentage / 100) * cameraImageWidth;

  const handleTitleContainerHeight = () => {
    if (!viewportContainerRef?.current) return;

    const viewportContainerBottom =
      viewportContainerRef.current.getBoundingClientRect().bottom;
    const newTitleContainerHeight = Math.floor(
      window.innerHeight -
        (viewportBottomOffset + (viewportContainerBottom - viewportHeight))
    );

    if (newTitleContainerHeight <= viewportHeight)
      setTitleContainerHeight(newTitleContainerHeight);
  };

  useMotionValueEvent(smoothScrollYProgress, 'change', (value) => {
    setIsVideoThumbnailCloneDisplayed(value >= 0.5);
    setIsVideoDisplayed(value >= 0.7);
    handleTitleContainerHeight();
  });

  useEffect(() => {
    const productNavigationElement =
      document.getElementById('ProductNavigation');
    if (!scrollableContainerRef?.current || !productNavigationElement) {
      setIsComponentOnTop(false);
      return;
    }

    const breadcrumbsElement = document.getElementById('Breadcrumbs');
    const firstContainerOnPage = breadcrumbsElement
      ? breadcrumbsElement.nextElementSibling
      : productNavigationElement.nextElementSibling;
    const isImmersiveVideoOnTop = firstContainerOnPage
      ?.querySelector('section')
      ?.contains(scrollableContainerRef.current);

    setIsComponentOnTop(isImmersiveVideoOnTop);
  }, []);

  useEffect(() => {
    if (isVideoDisplayed) scrollableContainerRef.current?.focus();
  }, [isVideoDisplayed]);

  useEffect(() => {
    if (videoIsFocusedByTab)
      scrollableContainerRef.current?.scrollIntoView({ block: 'end' });
  }, [videoIsFocusedByTab]);

  useEffect(() => {
    if (maxCameraScale === 0) return;

    setCameraImageWidth(maxCameraScale * initialCameraImageWidth);
    setCameraImageHeight(maxCameraScale * initialCameraImageHeight);
    setIsCameraImageDisplayed(true);
  }, [maxCameraScale, initialCameraImageWidth, initialCameraImageHeight]);

  return (
    <section
      className="component-padding bg-black-100 text-white"
      data-testid="CT-151-Immersive-Video-Section"
    >
      <SoftLoadWrapper>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isMobile === undefined ? 0 : 1 }}
          className="relative"
          style={{
            paddingBottom: isMobile ? textHeight + 6 : 0,
          }}
        >
          <div
            className={isPreview ? 'h-[2000px]' : 'h-[350vh]'}
            ref={scrollableContainerRef}
            data-testid="CT-151-Immersive-Video-Scrollable-Container"
          >
            <div
              className="sticky flex items-center justify-center overflow-hidden"
              style={
                isPreview
                  ? { height: 1080, top: 0 }
                  : {
                      height: `calc(100vh - ${viewportTopOffset}px - ${viewportBottomOffset}px)`,
                      top: viewportTopOffset,
                    }
              }
              ref={(node) => {
                viewportContainerRef.current = node;
                viewportContainerRefCallback(node);
              }}
              data-testid="CT-151-Immersive-Video-Sticky-Container"
            >
              <AnimatePresence>
                {isComponentOnTop !== undefined && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      ...(isComponentOnTop
                        ? { height: titleContainerHeight }
                        : {}),
                    }}
                    transition={{
                      opacity: { duration: 0.3 },
                      ...(isComponentOnTop
                        ? { height: { duration: 0.2 } }
                        : {}),
                    }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    {glow !== GlowColourEnum.None && (
                      <img
                        src={`${glow === GlowColourEnum.Yellow ? HueYellow : HueRed}`}
                        alt=""
                        aria-hidden="true"
                        className="absolute z-[-1] scale-x-[2] scale-y-[0.5] object-cover opacity-30 md:scale-x-[3]"
                        data-testid="CT-151-Immersive-Video-Glow"
                      />
                    )}
                    <p
                      ref={titleRef}
                      className={classNames(
                        'w-3/5 text-center md:w-4/5',
                        isMobile ? 'mount' : 'strap'
                      )}
                      data-testid="CT-151-Immersive-Video-Title"
                    >
                      {title}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div
                className="absolute flex opacity-0"
                ref={initialCameraImageRef}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: div must be focusable for keyboard navigation
                tabIndex={0}
                onFocus={() => setVideoIsFocusedByTab(true)}
              >
                <Image
                  image={cameraImage}
                  imageClasses={classNames(
                    'w-auto h-auto',
                    isPreview
                      ? 'max-h-[536px] max-w-[670px]'
                      : 'max-h-[60vh] max-w-[80vw]'
                  )}
                  classes="w-full"
                  dataTestId="CT-151-Immersive-Video-Hidden-Camera-Image"
                />
              </div>

              <motion.div
                className="absolute flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: isCameraImageDisplayed ? 1 : 0 }}
                style={{
                  width: cameraImageWidth,
                  height: cameraImageHeight,
                  scale: cameraImageScale,
                  transformOrigin: `${cameraImageTransformOriginLeft}% ${cameraImageTransformOriginTop}%`,
                  bottom: cameraImageBottomPosition,
                  left: cameraImageLeftPosition,
                }}
              >
                <Image
                  image={cameraImage}
                  imageClasses="w-auto h-full"
                  classes="w-full"
                  dataTestId="CT-151-Immersive-Video-Camera-Image"
                />
              </motion.div>

              <motion.div
                className="absolute inset-0 bg-black-100"
                style={{ opacity: cameraImageOpacity }}
                data-testid="CT-151-Immersive-Video-Camera-Image-Opacity"
              />

              <motion.div
                className="absolute flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: isCameraImageDisplayed ? 1 : 0 }}
                style={{
                  width: cameraImageWidth,
                  height: cameraImageHeight,
                  scale: cameraImageScale,
                  transformOrigin: `${cameraImageTransformOriginLeft}% ${cameraImageTransformOriginTop}%`,
                  bottom: cameraImageBottomPosition,
                  left: cameraImageLeftPosition,
                }}
              >
                <motion.div
                  className="absolute overflow-hidden"
                  style={{
                    top: videoThumbnailTopValue,
                    left: videoThumbnailLeftValue,
                    width: videoThumbnailWidth,
                    height: videoThumbnailHeight,
                    borderRadius: 5 * maxCameraScale,
                  }}
                >
                  <Image
                    image={videoThumbnail}
                    imageClasses="w-full h-full object-cover"
                    dataTestId="CT-151-Immersive-Video-Thumbnail-Image"
                  />
                </motion.div>
              </motion.div>

              {isVideoThumbnailCloneDisplayed && !isPreview && (
                <motion.div
                  className="absolute inset-0"
                  style={{
                    paddingTop: videoThumbnailCloneYPadding,
                    paddingBottom: videoThumbnailCloneYPadding,
                    paddingLeft: videoThumbnailCloneXPadding,
                    paddingRight: videoThumbnailCloneXPadding,
                  }}
                  tabIndex={0}
                >
                  <motion.div
                    className="h-full w-full overflow-hidden"
                    style={{
                      borderRadius: videoThumbnailCloneBorderRadius,
                    }}
                  >
                    <Image
                      image={videoThumbnail}
                      imageClasses="w-full h-full object-cover"
                      dataTestId="CT-151-Immersive-Video-Thumbnail-Image-Clone"
                    />
                  </motion.div>
                </motion.div>
              )}

              <AnimatePresence>
                {(isVideoDisplayed || videoIsFocusedByTab) && !isPreview && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.5,
                    }}
                    className="absolute inset-0"
                  >
                    <InViewVideo
                      hasInVideoPlayButton
                      inVideoPlayButtonPositionClassName="top-2.5 left-2.5 md:top-5 md:left-5"
                      inVideoPlayButtonClassName="z-10"
                      wrapperClasses="h-full"
                      videoClasses="[&>div>video]:object-cover h-full"
                      video={video}
                      autoPlay
                      controls={false}
                      onFocusCallback={(e) => {
                        if (e.target.matches(':focus-visible'))
                          setVideoIsFocusedByTab(!videoIsFocusedByTab);
                      }}
                      inViewThreshold={0}
                      dataTestId="CT-151-Immersive-Video-In-View-Video"
                    />

                    <motion.div
                      className="absolute inset-0 bg-black-100/20"
                      data-testid="CT-151-Immersive-Video-In-View-Video-Opacity"
                      style={{ opacity: videoGradientOpacity }}
                    />

                    <motion.div
                      className="absolute bottom-0 h-2/5 w-full bg-gradient-to-t from-black-100/100 md:from-black/100"
                      data-testid="CT-151-Immersive-Video-In-View-Video-Gradient"
                      style={{ opacity: textGradientOpacity }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <motion.div
            ref={textDetailsRef}
            className="absolute flex w-full items-center justify-center max-md:px-5"
            style={{
              bottom: textDetailsBottomPosition,
            }}
          >
            <div
              className="flex flex-col items-center justify-center text-center md:w-1/2 lg:w-1/3"
              ref={textContainerRef}
            >
              <p
                className="h1 mb-5 md:mb-10"
                data-testid="CT-151-Immersive-Video-Subtitle"
              >
                {subTitle}
              </p>
              <p
                ref={textRef}
                className="h6"
                data-testid="CT-151-Immersive-Video-Text"
              >
                {text}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </SoftLoadWrapper>
    </section>
  );
};

export default ImmersiveVideo;
